import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Send, Trash2, Bookmark, MapPin, Camera } from "lucide-react";
import {
  getGuestbook,
  addGuestbookNote,
  deleteGuestbookNote,
  getBookmarks,
  addBookmark,
  deleteBookmark,
} from "../lib/api";
import { useStore, getEffectiveWeather } from "../store";
import { cameraState } from "../three/cameraState";
import { LANDMARKS } from "../three/layout";

export default function Guestbook() {
  const open = useStore((s) => s.guestbookOpen);
  const setOpen = useStore((s) => s.setGuestbookOpen);
  const [tab, setTab] = useState("notes");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [landmark, setLandmark] = useState("North Pool");
  const [bmLabel, setBmLabel] = useState("");
  const qc = useQueryClient();

  const notes = useQuery({ queryKey: ["guestbook"], queryFn: () => getGuestbook(), enabled: open });
  const bookmarks = useQuery({ queryKey: ["bookmarks"], queryFn: getBookmarks, enabled: open });

  const addNote = useMutation({
    mutationFn: addGuestbookNote,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["guestbook"] });
      setName("");
      setMessage("");
      toast.success("Memorial note added");
    },
    onError: () => toast.error("Could not save note"),
  });
  const delNote = useMutation({
    mutationFn: deleteGuestbookNote,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["guestbook"] }),
    onError: () => toast.error("Could not delete note"),
  });
  const addBm = useMutation({
    mutationFn: addBookmark,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bookmarks"] });
      setBmLabel("");
      toast.success("View bookmarked");
    },
    onError: () => toast.error("Could not save bookmark"),
  });
  const delBm = useMutation({
    mutationFn: deleteBookmark,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bookmarks"] }),
    onError: () => toast.error("Could not delete bookmark"),
  });

  const saveView = () => {
    const s = useStore.getState();
    addBm.mutate({
      label: bmLabel || `View ${new Date().toLocaleTimeString()}`,
      position: cameraState.position,
      target: cameraState.target,
      temporal: s.temporal,
      time_of_day: s.timeOfDay,
      weather: getEffectiveWeather(s),
    });
  };

  const loadBm = (b) => {
    const s = useStore.getState();
    s.setNavMode("orbit");
    s.setTemporal(b.temporal);
    s.setTimeOfDay(b.time_of_day);
    s.setWeather(b.weather);
    s.requestFocus({ position: b.position, target: b.target });
    setOpen(false);
    toast.success(`Flying to “${b.label}”`);
  };

  const submitNote = (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) return;
    const era = useStore.getState().temporal < 2013.5 ? "2001" : "2026";
    addNote.mutate({ name: name.trim(), message: message.trim(), landmark, era });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.div
            data-testid="guestbook-modal"
            initial={{ scale: 0.92, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="relative w-full max-w-lg glass-light rounded-3xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[#D4AF37]/20">
              <div>
                <h2 className="font-serif-display text-2xl text-[#1c1a17] font-light">Reflections</h2>
                <p className="font-mono-tele text-[9px] tracking-widest text-stone-500 uppercase">
                  Leave a note · save a vantage
                </p>
              </div>
              <button data-testid="guestbook-close-btn" onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-black/5 text-stone-600">
                <X size={18} />
              </button>
            </div>

            <div className="flex gap-1 px-6 pt-3">
              {[
                ["notes", "Memorial Notes"],
                ["bookmarks", "Camera Bookmarks"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`guestbook-tab-${k}`}
                  onClick={() => setTab(k)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    tab === k ? "bg-[#D4AF37] text-[#0b0c10]" : "text-stone-600 hover:bg-black/5"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === "notes" ? (
              <div className="px-6 py-4">
                <form onSubmit={submitNote} className="space-y-2.5">
                  <div className="flex gap-2">
                    <input
                      data-testid="guestbook-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="flex-1 rounded-xl border border-stone-300 bg-white/70 px-3 py-2 text-sm text-stone-800 outline-none focus:border-[#D4AF37]"
                    />
                    <select
                      data-testid="guestbook-landmark-select"
                      value={landmark}
                      onChange={(e) => setLandmark(e.target.value)}
                      className="rounded-xl border border-stone-300 bg-white/70 px-2 py-2 text-sm text-stone-800 outline-none focus:border-[#D4AF37]"
                    >
                      {LANDMARKS.map((l) => (
                        <option key={l.key}>{l.key}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    data-testid="guestbook-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="A reflection, a memory, a tribute…"
                    rows={2}
                    className="w-full rounded-xl border border-stone-300 bg-white/70 px-3 py-2 text-sm text-stone-800 outline-none focus:border-[#D4AF37] resize-none"
                  />
                  <button
                    data-testid="guestbook-form-submit-btn"
                    type="submit"
                    disabled={addNote.isPending}
                    className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#1c1a17] text-white py-2.5 text-sm font-medium hover:bg-[#997316] transition-colors"
                  >
                    <Send size={15} /> Place Note
                  </button>
                </form>

                <div className="mt-4 max-h-56 overflow-y-auto hud-scroll space-y-2">
                  {notes.data?.length ? (
                    notes.data.map((n) => (
                      <div key={n.id} data-testid={`guestbook-note-${n.id}`} className="group rounded-xl bg-white/60 border border-stone-200 px-3 py-2">
                        <div className="flex items-center justify-between">
                          <span className="font-serif-display text-base text-[#1c1a17]">{n.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1 font-mono-tele text-[9px] text-[#997316] uppercase tracking-widest">
                              <MapPin size={10} /> {n.landmark}
                            </span>
                            <button data-testid={`guestbook-note-delete-${n.id}`} onClick={() => delNote.mutate(n.id)} className="opacity-60 sm:opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-stone-700 mt-0.5">{n.message}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-sm text-stone-400 py-6">Be the first to leave a reflection.</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="px-6 py-4">
                <div className="flex gap-2">
                  <input
                    data-testid="bookmark-label-input"
                    value={bmLabel}
                    onChange={(e) => setBmLabel(e.target.value)}
                    placeholder="Name this vantage point"
                    className="flex-1 rounded-xl border border-stone-300 bg-white/70 px-3 py-2 text-sm text-stone-800 outline-none focus:border-[#D4AF37]"
                  />
                  <button
                    data-testid="bookmark-save-btn"
                    onClick={saveView}
                    className="flex items-center gap-2 rounded-xl bg-[#1c1a17] text-white px-3 py-2 text-sm font-medium hover:bg-[#997316] transition-colors"
                  >
                    <Camera size={15} /> Save
                  </button>
                </div>
                <div className="mt-4 max-h-64 overflow-y-auto hud-scroll space-y-2">
                  {bookmarks.data?.length ? (
                    bookmarks.data.map((b) => (
                      <div key={b.id} data-testid={`bookmark-${b.id}`} className="group flex items-center justify-between rounded-xl bg-white/60 border border-stone-200 px-3 py-2">
                        <button onClick={() => loadBm(b)} className="flex items-center gap-2 text-left flex-1">
                          <Bookmark size={14} className="text-[#D4AF37]" />
                          <div>
                            <div className="text-sm text-[#1c1a17]">{b.label}</div>
                            <div className="font-mono-tele text-[9px] text-stone-500 uppercase tracking-widest">
                              {Math.floor(b.temporal)} · {b.weather}
                            </div>
                          </div>
                        </button>
                        <button data-testid={`bookmark-delete-${b.id}`} onClick={() => delBm.mutate(b.id)} className="opacity-60 sm:opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-sm text-stone-400 py-6">Save a view to return to it later.</p>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
